import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import dig from 'object-dig';
import moment from 'moment';
import { toast } from 'react-toastify';

import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PeopleIcon from '@material-ui/icons/People';
import SaveAlt from '@material-ui/icons/SaveAlt';

import Chip from '@material-ui/core/Chip';
import LinkMaterial from '@material-ui/core/Link';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Typography from '@material-ui/core/Typography';

import Paper from '~/components/Paper';
import Button from '~/components/Button';
import Table from '~/components/Table/Table';
import GridItem from '~/components/GridItem';
import InputDate from '~/components/Input/InputDate';
import GridContainer from '~/components/GridContainer';
import InputSearch from '~/components/Input/InputSearch';
import LinearProgress from '~/components/LinearProgress';
import tableStyles from '~/components/Table/Table/styles';
import InputControl from '~/components/Input/InputControl';
import TablePagination from '~/components/Table/TablePagination';
import InputSelect from '~/components/Input/InputSelect';
import InputMultiCorporation from '~/components/Input/InputMultiCorporation';

import CurrentUser from '~/contexts/CurrentUser';

import Request from '~/providers/Request';
import { showMedicalAppointments } from '~/config/routes';

import toInt from '~/utils/Number/toInt';
import isPresent from '~/utils/isPresent';
import csvGenerator from '~/utils/csvGenerator';
import xlsxGenerator from '~/utils/xlsxGenerator';
import fetchAllCorporatePartner from '~/utils/fetchAllCorporatePartner';

const NO_SERVICE_EXIT = 'none';

const serviceExitsParam = (exits) =>
  isPresent(exits) ? JSON.stringify(exits.map((exit) => exit.id)) : null;

function SIDMedicalAppointmentsReports() {
  const { t } = useTranslation();
  const classesTable = tableStyles();

  const [loading, setLoading] = useState(false);
  const [userActives, setUserActives] = useState([]);
  const [totalMedicalAppointments, settotalMedicalAppointments] = useState(0);

  const [corporation, setCorporation] = useState(null);
  const [dataInit, setDataInit] = useState(moment().startOf('month').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('month').format());

  const [specialtiesOptions, setSpecialtiesOptions] = useState([]);

  const [specialty, setSpecialty] = useState({ id: 0, name: t('all') });

  const [serviceExitOptions, setServiceExitOptions] = useState([]);
  const [serviceExits, setServiceExits] = useState([]);
  const [ignoreServiceExits, setIgnoreServiceExits] = useState([]);

  const [inputSearch, setInputSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [fileType, setFileType] = useState('');

  const { user } = useContext(CurrentUser);

  const trAttr = (attr) =>
    t(`SIDMedicalAppointmentsReports.attributes.${attr}`);

  async function getFirstCorporation() {
    fetchAllCorporatePartner(null, 0, (data) => {
      setCorporation([
        {
          corporation_id: null,
          id: null,
          name: 'Todas',
        },
      ]);
    });
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(event.target.value);
    setPage(0);
  };

  async function fetchSpecialties() {
    if (corporation !== null) {
      const dataPromises = corporation.map(async (corp) => {
        const data = await Request.get(
          '/admin/specialties',
          {
            corporation_id: corp.corporation_id,
          },
          { normalizeJSON: true }
        );
        return data.data;
      });

      Promise.all(dataPromises)
        .then((data) => {
          const allData = data.flat();

          const arr = [{id: 0, name: t('all')}, ...allData];
          const uniqueArr = [...new Map(arr.map(item =>
            [item.id, item])).values()];

          setSpecialtiesOptions(uniqueArr);
        })
        .catch((error) => {
          console.error('Error fetching data:', error);
        });
    }
  }

  function fetchServiceExits() {
    Request.get('/admin/service_exits', { sorter: 'id.asc', per_page: 100 })
      .then(({ data }) => {
        setServiceExitOptions([
          ...data.map((exit) => ({
            id: Number(exit.id),
            name: exit.description,
          })),
          {
            id: NO_SERVICE_EXIT,
            name: t('SIDMedicalAppointmentsReports.extras.no_service_exit'),
          },
        ]);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  async function generateFile(filters, type) {
    try {
      const appointments = await Request.get(
        '/admin/report/medical_appointments_report',
        filters,
        {
          normalizeJSON: false,
        }
      );

      const rows = [];

      rows.push(['Consultas Realizadas']);
      rows.push(['']);

      rows.push([
        'Período',
        `${moment(filters.start).format('DD/MM/YYYY HH:mm')} a ${moment(
          filters.end
        ).format('DD/MM/YYYY HH:mm')}`,
      ]);
      rows.push(['Total de consultas', appointments.data.length]);
      rows.push(['']);
      rows.push(['Consultas']);
      rows.push([
        'ID',
        'Cadastro do usuário',
        'Nome',
        'CPF',
        'Especialidade',
        'Início da consulta',
        'Fim da consulta',
        'Tempo de consulta',
        'Fim do atendimento',
        'Médico',
        'Empresa',
        'Saída de Atendimento',
      ]);

      appointments.data.map((item) => {
        if (item.specialty_id === null) {
          item.specialty = 'Clínico Geral';
        }

        const baseDoctorEndTime = moment(item.doctor_end_at);
        const baseDoctorInitTime = moment(item.doctor_init_at);

        const doctorEndTime = item.doctor_end_at
          ? baseDoctorEndTime.format('DD/MM/YYYY HH:mm')
          : null;
        const doctorInitTime = item.doctor_init_at
          ? baseDoctorInitTime.format('DD/MM/YYYY HH:mm')
          : null;

        const startMinute = baseDoctorInitTime.startOf('minutes');
        const endMinute = baseDoctorEndTime.startOf('minutes');

        const consultationTime =
          item.doctor_init_at && item.doctor_end_at
            ? moment
                .utc(moment(endMinute).diff(moment(startMinute)))
                .format('HH:mm')
            : null;

        rows.push([
          item.id,
          item.created_at
            ? moment(item.created_at).format('DD/MM/YYYY HH:mm')
            : null,
          item.name,
          item.cpf,
          item.specialty,
          doctorInitTime,
          doctorEndTime,
          consultationTime,
          item.attendant_end_at
            ? moment(item.attendant_end_at).format('DD/MM/YYYY HH:mm')
            : null,
          item.doctor,
          item.user_corporation_name,
          item.service_exit,
        ]);
        return item;
      });
      rows.push(['']);
      rows.push(['Relatório gerado por', user.name]);
      rows.push([
        'Relatório gerado em',
        `${moment().format('L')} - ${moment().format('LT')}`,
      ]);

      if (type === 'csv') {
        csvGenerator('Relatório_consultas_realizadas.csv', [...rows]);
      } else {
        xlsxGenerator('Relatório_consultas_realizadas_.xlsx', [...rows]);
      }
      toast.success(
        `Arquivo ${type.toLocaleUpperCase()} de clientes gerado com sucesso!`
      );

      const totalPages =
        toInt(appointments.meta.total) / appointments.meta.perPage;
      if (appointments.meta.page < totalPages) {
        filters.page += 1;
        generateFile(filters, type);
      }
    } catch (error) {
      console.log(error);
      toast.error(
        `Não foi possível gerar o arquivo ${type.toLocaleUpperCase()} dos clientes`
      );
    }
  }

  function onExport(type) {
    const corpsId = corporation
      ? `[${corporation.map((corp) => corp.id).join(',')}]`
      : null;
    const corpsCorporationId = corporation
      ? `[${corporation.map((corp) => corp.corporation_id).join(',')}]`
      : null;

    const filters = {
      start: dataInit,
      end: dataEnd,
      qs: inputSearch,
      page: 1,
      per_page: 50000,
      specialty_id: dig(specialty, 'id'),
      partners: corpsId,
      corporations: corpsCorporationId,
      consider_branches: true,
      service_exits: serviceExitsParam(serviceExits),
      ignore_service_exits: serviceExitsParam(ignoreServiceExits),
    };

    toast.info(`Arquivo ${type.toLocaleUpperCase()} esta sendo gerado!`);
    generateFile(filters, type);
  }

  async function getMedicalAppointments() {
    setLoading(true);

    const corpsId = corporation
      ? `[${corporation.map((corp) => corp.id).join(',')}]`
      : null;
    const corpsCorporationId = corporation
      ? `[${corporation.map((corp) => corp.corporation_id).join(',')}]`
      : null;

    const filters = {
      start: dataInit,
      end: dataEnd,
      qs: inputSearch,
      page: page + 1,
      per_page: rowsPerPage,
      specialty_id: dig(specialty, 'id'),
      partners: corpsId,
      corporations: corpsCorporationId,
      consider_branches: true,
      service_exits: serviceExitsParam(serviceExits),
      ignore_service_exits: serviceExitsParam(ignoreServiceExits),
    };

    Request.get('/admin/report/medical_appointments_report', filters, {
      normalizeJSON: false,
    })
      .then((response) => {
        const { data, meta } = response;
        setLoading(false);
        setUserActives(data);
        setPage(toInt(meta.page) - 1);
        settotalMedicalAppointments(toInt(meta.total));
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }

  async function fetchParams() {
    const url = new URLSearchParams(window.location.search);

    const start = url.get('start');
    const end = url.get('end');
    const corporation_id = url.get('corporation_id');

    if (isPresent(start)) {
      const startDate = moment(start);

      if (startDate.isValid()) {
        setDataInit(startDate.format());
      }
    }
    if (isPresent(end)) {
      const endDate = moment(end);

      if (endDate.isValid()) {
        setDataEnd(endDate.format());
      }
    }
    if (isPresent(corporation_id)) {
      try {
        const { data } = await Request.get(
          `/admin/partner/${corporation_id}`,
          null,
          {
            normalizeJSON: true,
          }
        );

        setCorporation(data);
      } catch (error) {
        console.log(error);
        toast.info(
          'Não foi possível encontrar a empresa informada por parâmetro'
        );
      }
    } else {
      getFirstCorporation();
    }
  }

  useEffect(() => {
    fetchSpecialties(corporation);
  }, [corporation]);

  useEffect(() => {
    if (corporation && dataInit && dataEnd && specialty) {
      getMedicalAppointments();
    }
  }, [
    corporation,
    inputSearch,
    page,
    rowsPerPage,
    dataInit,
    dataEnd,
    specialty,
    serviceExits,
    ignoreServiceExits,
  ]);
  useEffect(() => {
    fetchParams();
    fetchServiceExits();
  }, []);
  return (
    <>
      <Paper
        Icon={PeopleIcon}
        iconColor="blue"
        title={t('dashboard.attributes.SIDMedicalAppointmentsReports')}
        subtitle={t(
          'dashboard.attributes.SIDMedicalAppointmentsReportsSubTitle'
        )}
      >
        <GridContainer>
          <GridItem xs={12} sm={12} md={12} lg={12} xl={12}>
            <InputMultiCorporation
              corporation={corporation}
              setCorporation={setCorporation}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataInit}
              name="date"
              label={t('SIDMedicalAppointmentsReports.extras.date_init')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) => {
                setDataInit(moment(date).startOf('day').format());
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={3} md={3} lg={3} xl={3}>
            <InputControl
              value={dataEnd}
              name="date"
              label={t('SIDMedicalAppointmentsReports.extras.date_end')}
              inputComponent={InputDate}
              InputProps={{
                type: 'date',
              }}
              onChange={(date) =>
                setDataEnd(moment(date).endOf('day').format())
              }
            />
          </GridItem>
        </GridContainer>

        <GridContainer>
          <GridItem xs={12} sm={4} md={4} lg={8} xl={4}>
            <InputSearch
              value={inputSearch}
              label={t('action.search')}
              placeholder={t('SIDMedicalAppointmentsReports.extras.search')}
              onChange={setInputSearch}
            />
          </GridItem>
          <GridItem xs={12} sm={4} md={4} lg={4} xl={4}>
            <InputControl
              name={'specialty'}
              value={specialty}
              label={t('packageEspecialtyActives.extras.specialty')}
              inputComponent={InputSelect}
              onChange={({ specialty }) => {
                setSpecialty(specialty);
              }}
              InputProps={{
                isClearable: false,
                options: specialtiesOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputControl
              name="serviceExits"
              value={serviceExits}
              label={trAttr('service_exit')}
              inputComponent={InputSelect}
              onChange={({ serviceExits: exits }) => {
                setServiceExits(exits || []);
                setPage(0);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: serviceExitOptions,
                outValueType: 'object',
                placeholder: t('all'),
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputControl
              name="ignoreServiceExits"
              value={ignoreServiceExits}
              label={t(
                'SIDMedicalAppointmentsReports.extras.ignore_service_exit'
              )}
              inputComponent={InputSelect}
              onChange={({ ignoreServiceExits: exits }) => {
                setIgnoreServiceExits(exits || []);
                setPage(0);
              }}
              InputProps={{
                isClearable: true,
                isMulti: true,
                options: serviceExitOptions,
                outValueType: 'object',
              }}
            />
          </GridItem>
        </GridContainer>
        <GridContainer>
          <GridItem align="right">
            <Button
              // style={{ margin: 20 }}
              color="second"
              onClick={() => {
                setFileType('csv');
                onExport('csv');
              }}
            >
              {t('action.export_csv')} <SaveAlt />
            </Button>
            <Button
              style={{ marginLeft: 10 }}
              color="orange"
              onClick={() => {
                setFileType('xlsx');
                onExport('xlsx');
              }}
            >
              {t('action.export_xlsx')} <SaveAlt />
            </Button>
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <Table
          emptyData={userActives.length === 0}
          loading={loading}
          columns={
            <>
              <TableCell className={classesTable.cell}>
                {trAttr('id')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('created_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('name')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('cpf')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('specialty')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('doctor_init_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('doctor_end_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('consultation_time')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('attendant_end_at')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('doctor')}
              </TableCell>
              <TableCell className={classesTable.cell}>
                {trAttr('service_exit')}
              </TableCell>
            </>
          }
        >
          {userActives.map((row, index) => {
            if (row.specialty_id === null) {
              row.specialty = 'Clínico Geral';
            }

            const baseDoctorEndTime = moment(row.doctor_end_at);
            const baseDoctorInitTime = moment(row.doctor_init_at);

            const doctorEndTime = row.doctor_end_at
              ? baseDoctorEndTime.format('DD/MM/YYYY HH:mm')
              : null;
            const doctorInitTime = row.doctor_init_at
              ? baseDoctorInitTime.format('DD/MM/YYYY HH:mm')
              : null;

            const startMinute = baseDoctorInitTime.startOf('minutes');
            const endMinute = baseDoctorEndTime.startOf('minutes');

            const consultationTime =
              row.doctor_init_at && row.doctor_end_at
                ? moment
                    .utc(moment(endMinute).diff(moment(startMinute)))
                    .format('HH:mm')
                : null;

            return (
              <TableRow key={index} hover className={classesTable.row}>
                <TableCell>
                  <Chip
                    label={row.id}
                    component={LinkMaterial}
                    href={showMedicalAppointments.reverse({
                      id: row.id,
                    })}
                    style={{ cursor: 'pointer' }}
                    target="_blank"
                    color="primary"
                    variant="outlined"
                    onDelete={() => {}}
                    deleteIcon={<OpenInNewIcon />}
                  />
                </TableCell>
                <TableCell>
                  {row.created_at
                    ? moment(row.created_at).format('DD/MM/YYYY HH:mm')
                    : null}
                </TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.cpf}</TableCell>

                <TableCell>{row.specialty}</TableCell>
                <TableCell>{doctorInitTime}</TableCell>
                <TableCell>{doctorEndTime}</TableCell>
                <TableCell>{consultationTime}</TableCell>
                <TableCell>
                  {row.attendant_end_at
                    ? moment(row.attendant_end_at).format('DD/MM/YYYY HH:mm')
                    : null}
                </TableCell>
                <TableCell>{row.doctor}</TableCell>
                <TableCell>{row.service_exit}</TableCell>
              </TableRow>
            );
          })}
        </Table>

        <LinearProgress loading={loading} />

        <Typography
          variant="subtitle2"
          color="primary"
          align="right"
          style={{ paddingRight: 16, marginTop: 10 }}
        >
          <strong>Total: </strong>
          {totalMedicalAppointments}
        </Typography>

        <TablePagination
          count={totalMedicalAppointments}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </>
  );
}

export default SIDMedicalAppointmentsReports;
